import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';

export function useErrorCapture(sessionId: string | null) {
  const { data: appUser } = useCurrentAppUser();
  const location = useLocation();
  const recentErrorsRef = useRef<Set<string>>(new Set());

  const captureError = useCallback(async (
    error: Error,
    componentStack?: string
  ) => {
    if (!appUser?.id) return;

    // Deduplicate errors within a short window
    const errorKey = `${error.message}-${error.stack?.slice(0, 100)}`;
    if (recentErrorsRef.current.has(errorKey)) return;
    recentErrorsRef.current.add(errorKey);

    // Clear the error from the set after 5 seconds to allow re-reporting
    setTimeout(() => {
      recentErrorsRef.current.delete(errorKey);
    }, 5000);

    try {
      const { error: insertError } = await supabase.from('frontend_errors').insert({
        session_id: sessionId,
        user_id: appUser.id,
        error_message: error.message.slice(0, 1000), // Limit message length
        error_stack: error.stack?.slice(0, 5000), // Limit stack length
        error_type: error.name,
        page_path: location.pathname,
        component_stack: componentStack?.slice(0, 5000),
        user_agent: navigator.userAgent,
        browser_info: {
          language: navigator.language,
          platform: navigator.platform,
          cookieEnabled: navigator.cookieEnabled,
          screenWidth: screen.width,
          screenHeight: screen.height,
        },
      });

      if (insertError) {
        console.error('[ErrorCapture] Failed to log error:', insertError);
      } else {
        console.log('[ErrorCapture] Error captured:', error.message);
      }
    } catch (err) {
      console.error('[ErrorCapture] Failed to log error:', err);
    }
  }, [appUser?.id, sessionId, location.pathname]);

  // Global error handler
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      captureError(new Error(event.message));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error 
        ? event.reason 
        : new Error(String(event.reason));
      captureError(error);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [captureError]);

  return { captureError };
}
